// src/lib/db/schema.ts
//
// ═══════════════════════════════════════════════════════════════════════════
//  FROZEN CONTRACT — SPEC §3.3, the v1 schema freeze. M0's deliverable.
//
//  Every later milestone and every user's stored data depends on this file.
//  A mistake here is a migration on every device, forever. Change it only
//  through a `version(n).stores()` bump with the §3.7 migration policy
//  (pre-migration snapshot, /repair route, and the committed v1 fixtures
//  still importing green).
//
//  Three invariants that are NOT stylistic and must never be relaxed:
//
//   1. IndexedDB cannot index booleans, `null` or `undefined`. A row whose
//      indexed keypath holds one of those is simply ABSENT from that index —
//      silently. So every flag on this schema is `0 | 1`, every absent
//      timestamp is `0`, and EVERY INDEXED FOREIGN KEY IS `''` WHEN ABSENT,
//      never `null`. Never `deleted: false`. Never `deleted_at: null`.
//      Never `collection_id: null`.
//
//      SPEC DISAGREEMENT, DECIDED IN M0. §3.3 types four of these as
//      `string | null` AND indexes all four, eight lines below the paragraph
//      that forbids exactly that: `Note.collection_id`, `Highlight.note_id`,
//      `Bookmark.collection_id` (also inside `[collection_id+sort_key]`) and
//      `Collection.parent_id`. Verified against fake-indexeddb: two bookmarks
//      written, one with `collection_id: null`, and `orderBy('[collection_id+
//      sort_key]')` returns ONE of the two — the uncategorised bookmark is
//      invisible in every list built on that index. And
//      `where('parent_id').equals(null)` THROWS `Invalid key provided`, so
//      "list the root collections" — the first query the Collections screen
//      makes — cannot be written against the index at all.
//
//      `''` is a valid IndexedDB key, sorts before every UUIDv7, and makes
//      `where('parent_id').equals('')` the natural roots query. The pair
//      (nullable + indexed) cannot both stand, and every one of these is an
//      index in a `version(1).stores()` string, so correcting it after ship is
//      a version bump on every installed device. It is corrected here.
//
//      `NoteRevision.collection_id` follows the same rule although it is not
//      indexed: a revision is a SNAPSHOT of `Note.collection_id` and restoring
//      one writes that value straight back onto an indexed keypath.
//   2. Naturally-keyed facts get DETERMINISTIC ids (`${day}:${prayer}`,
//      `memo:${ayahId}`, `${note_id}:${tag_id}`), so two devices recording the
//      same fact produce the same row: not a conflict, and it cannot trip an
//      index on import. Genuinely independent creations get UUIDv7.
//   3. The `anchor.ref` string is the truth. `surah`, `ayah`, `word_start`,
//      `start_pos` and `end_pos` are derived and are rebuilt from `ref` on
//      import — which is what lets a v1.0 export import into v4.0.
// ═══════════════════════════════════════════════════════════════════════════

import Dexie, { type DexieOptions, type Table } from 'dexie';

export const SCHEMA_VERSION = 2;

/* The coordinate system is declared ONCE, in `src/lib/anchor/types.ts`, and
 * re-exported here. SPEC §3.3 writes these constants and the `Anchor` /
 * `Anchored` interfaces inline in this file and §1.2 also gives
 * `anchor/types.ts`; two structurally identical declarations of a FROZEN
 * contract is the one arrangement that can silently diverge, and a divergence
 * between the anchor codec's idea of `WORD_SLOTS` and the database's would
 * mis-place every note in the mushaf. Every existing import of
 * `ANCHOR_SPACE_VERSION` or `Anchor` from `$lib/db/schema` keeps working. */
export {
	ANCHOR_SPACE_VERSION,
	NUMBERING, // Hafs 'an 'Asim, Kufan count, 6236 ayat
	NO_POS,
	TOTAL_AYAT,
	WORD_SLOTS
} from '../anchor/types';
export type { Anchor, AnchorKind, Anchored, AyahId, Pos } from '../anchor/types';

import type { Anchor, Anchored, AyahId, Pos } from '../anchor/types';

/** The Dexie database name. One database, one origin, never deleted. */
export const DB_NAME = 'ghuraba';

/**
 * "No parent / no collection / no note", for every INDEXED foreign key.
 *
 * Not `null`: IndexedDB cannot index it, and a row whose indexed keypath is
 * `null` is silently ABSENT from that index — see invariant 1 above. `''` is a
 * valid key, sorts before every UUIDv7, and makes the roots query
 * `where('parent_id').equals(NO_PARENT)` instead of a full-table filter.
 *
 * Named rather than written inline so that a future reader greps one symbol and
 * finds every place the sentinel is meaningful.
 */
export const NO_REF = '';

/* ═══════════ 1. THE SYNC ENVELOPE ═══════════
 * On EVERY user row from v1, even though v1 ships no server.
 * Importing a backup from another device IS a sync: two devices, two files,
 * one merge. The reducer an import needs is the reducer a server delta needs.
 * Cost ~40 bytes/row; makes v2 a 60-line adapter with zero data-layer change.
 *
 * IndexedDB CANNOT index booleans, null or undefined — a row whose indexed
 * keypath is null is simply ABSENT from that index. So every flag is 0|1 and
 * every absent timestamp is 0. Never `deleted: false`.
 */
export interface Sync {
	id: string; // UUIDv7 — time-ordered, so [updated_at+id] scans stay cheap
	created_at: number;
	updated_at: number; // the LWW clock
	rev: number; // bumped on every local write
	base_rev: number; // rev this edit was based on — divergence detection
	deleted: 0 | 1;
	deleted_at: number; // 0 when live
	device_id: string; // UUIDv7, minted once per install, never rotated
}

/* ═══════════ 2. THE ANCHOR ═══════════
 * `AnchorKind` deliberately has no 'page' member. Pages are a rendering, not a
 * place. `Anchor.ref` is THE TRUTH — durable, portable, and the only anchor
 * field in the backup file; `surah`, `ayah`, `word_start`, `start_pos` and
 * `end_pos` are derived from it and rebuilt on import, which is what lets a
 * v1.0 export import correctly into v4.0. See `src/lib/anchor/types.ts`.
 */

/** The five anchor fields that cross the wire in a backup file (§3.6 note 2).
 *  Everything else on `Anchor` is derived from `ref` and rebuilt on import. */
export type PortableAnchor = Pick<
	Anchor,
	'ref' | 'quote' | 'text_hash_raw' | 'text_hash_fold' | 'anchor_space_version'
>;

export const PORTABLE_ANCHOR_FIELDS = [
	'ref',
	'quote',
	'text_hash_raw',
	'text_hash_fold',
	'anchor_space_version'
] as const satisfies readonly (keyof PortableAnchor)[];

/* ═══════════ 3. ENTITIES ═══════════ */

export interface Note extends Sync, Anchored {
	title: string; // '' when untitled
	body: string; // markdown. '' when private === 1.
	/** AES-256-GCM ciphertext (base64) of the markdown body when private === 1.
	 *  The quote, anchor, hashes, timestamps and tag links stay PLAINTEXT so
	 *  gutter markers, ordering, tag lenses and merge all still work, and
	 *  mergeRow treats this as an opaque string — the LWW reducer and the
	 *  SyncPort are untouched. One field, one codec. */
	body_enc: string; // '' when private === 0
	private: 0 | 1;
	body_dir: 'auto'; // rendered dir="auto" — notes are often bilingual
	/** `NO_REF` ('') when uncategorised — INDEXED, so never `null`. */
	collection_id: string;
	pinned: 0 | 1;
	/** Resurfacing scheduler (see §3.4). 0 = never resurface. */
	resurface_at: number;
}
// NOTE: there is no `tags: string[]` on this row. See `note_tags` below.

/** Display names for tags. Renaming a tag rewrites ONE row, not 400. */
export interface Tag extends Sync {
	name: string;
	color: string;
}

/** Set membership as its own rows, so it merges by UNION and cannot conflict —
 *  the same shape DhikrEvent already uses correctly. Row id is deterministic:
 *  `${note_id}:${tag_id}`, so two devices adding the same tag produce the same
 *  row and it is not a conflict. Tombstoned like everything else. */
export interface NoteTag extends Sync {
	note_id: string;
	tag_id: string;
}

/** APPEND-ONLY snapshot journal of EVERY user-authored field, not just body.
 *  - index 0 is PINNED and never evicted: the original text is the revision
 *    you most want after a bad merge.
 *  - capped by COUNT only (200), never by age: old notes have the most
 *    accumulated merge exposure, so an age cap evaporates the net exactly
 *    where it is needed.
 *  - EXPORTED BY DEFAULT, with an opt-out. A few hundred KB is nothing, and
 *    a net that does not survive the export/import round trip does not
 *    protect the one operation it exists for.
 *  - 'Clear history for this note' is ONE TAP in the History sheet: this is
 *    a plaintext record of a sentence the user deliberately deleted. */
export interface NoteRevision {
	id: string;
	note_id: string;
	idx: number; // idx 0 = original, pinned
	at: number;
	device_id: string;
	title: string;
	body: string;
	body_enc: string;
	private: 0 | 1;
	tag_ids: string[];
	/** `NO_REF` ('') when uncategorised. Restoring a revision writes this
	 *  straight back onto `Note.collection_id`, which IS indexed. */
	collection_id: string;
	pinned: 0 | 1;

	/** ADDED AT v2, AND THE REASON FOR THE BUMP.
	 *
	 *  v1 gave this table no tombstone. It is in `APPEND_ONLY_TABLES`, so it
	 *  merges by union — and import is merge-only by design, with no replace
	 *  path anywhere in the codebase. The two are individually correct and
	 *  jointly wrong: a user taps "clear history" (SPEC §2.1 calls it the thing
	 *  that stops the journal being "a privacy hazard wearing a safety
	 *  feature's clothes"), then restores ANY backup taken before the clear, and
	 *  every deleted sentence comes back. Permanently, because there is nothing
	 *  in the file to say it was ever cleared.
	 *
	 *  A tombstone is the only fix that survives a merge, because the union has
	 *  to be told. `MetaRow.key` is a closed union with no room for a per-note
	 *  watermark, so this could not be repaired from above the schema.
	 *
	 *  A tombstoned revision keeps its `id`, `note_id`, `idx` and `device_id`
	 *  and NOTHING ELSE: `title`, `body` and `body_enc` are emptied at the
	 *  moment of clearing, so the row that propagates carries no text. Clearing
	 *  is a privacy act; a tombstone that still held the sentence would defeat
	 *  its own purpose.
	 *
	 *  Tombstones are never evicted by the cap (`evictRevisions`) — an evicted
	 *  tombstone is a forgotten deletion, and the next import resurrects. */
	deleted: 0 | 1;
	deleted_at: number; // 0 when live
}

/**
 * Count cap for `note_revisions`, per note. By COUNT only, never by age: old
 * notes have the most accumulated merge exposure, so an age cap evaporates the
 * net exactly where it is needed.
 *
 * THE PIN RULE, RESPECIFIED. SPEC §3.3 says "index 0 is PINNED and never
 * evicted". That is unambiguous on ONE device and ambiguous after two devices'
 * journals merge: `idx` is a PER-DEVICE counter, so the merged journal has
 * duplicate `idx` values, gaps, and — routinely — TWO rows at `idx === 0`, one
 * per device. "Pin index 0" then has no single referent.
 *
 * The rule is therefore: PIN EVERY ROW WITH `idx === 0`, whatever device it
 * came from, then evict the rest by ASCENDING `at` until the note is at the
 * cap. Both originals are the thing you most want after a bad merge, and
 * keeping two of them costs nothing.
 *
 * `evictRevisions()` in `revisions.ts` is that rule; it is pure, so the policy
 * is testable without a database, and it is the only place the cap is applied.
 */
export const NOTE_REVISION_CAP = 200;

/** The LOSING side of any merge, for ANY table. Never discarded,
 *  never auto-resolved. Generic so Highlight.label, Collection.parent_id
 *  cycle breaks and future tables are covered without a new table each time. */
export interface Conflict {
	id: string;
	table: string; // 'notes' | 'highlights' | 'collections' | …
	row_id: string;
	reason: 'lww' | 'cycle' | 'quarantine';
	snapshot: string; // JSON of the entire losing row
	from_device: string;
	from_device_label: string;
	from_updated_at: number;
	created_at: number;
	resolved: 0 | 1;
}

export interface Highlight extends Sync, Anchored {
	color: 'amber' | 'green' | 'sky' | 'rose' | 'violet' | 'slate';
	style: 'fill' | 'underline';
	label: string; // '' — colour must never be the ONLY carrier of meaning
	/** `NO_REF` ('') for a standalone highlight — INDEXED, so never `null`. */
	note_id: string;
}

export interface Bookmark extends Sync, Anchored {
	label: string;
	/** `NO_REF` ('') when uncategorised — INDEXED, and also part of the
	 *  compound `[collection_id+sort_key]`, where a `null` removes the row
	 *  from the ordering entirely. */
	collection_id: string;
	/** Fractional index generated from LOCAL neighbours, so two devices can
	 *  independently mint the SAME key for different bookmarks. Namespacing it
	 *  by device makes order deterministic instead of silently corrupted.
	 *  Sorted by (sort_key, device_id). */
	sort_key: string;
}

export interface Collection extends Sync {
	name: string;
	color: string;
	icon: string;
	/** `NO_REF` ('') for a root collection — INDEXED, so never `null`.
	 *  CYCLE-CHECKED inside ingest — see §3.7 and `cycles.ts`. */
	parent_id: string;
	sort_key: string;
}

/** Pre-commit crash recovery. Keyed by anchor ref + device, cleared on commit.
 *  Covers the 400 ms before the first autosave and a sheet dismissed mid-word. */
export interface Draft {
	/** `${key}:${device_id}`, where `key` is `note:${note_id}` for a note that
	 *  exists and the anchor ref for a composer that has not created one yet.
	 *
	 *  v1 specified `${ref}:${device_id}` and could not distinguish two notes
	 *  that share an anchor. SPEC §3.4 designs for "six notes on 2:255"
	 *  explicitly, and every free-standing remark shares `ref === 'free:'` —
	 *  there is exactly one FREE_REF. One draft slot per (ref, device) therefore
	 *  meant two open composers addressing the SAME row: last keystroke wins,
	 *  and crash recovery restored a half-typed sentence into the WRONG note.
	 *
	 *  M3 mitigated it above the schema by namespacing the key space, which was
	 *  safe only because `drafts` is device-local, never exported, not in
	 *  `ANCHORED_TABLES`, and `Draft.ref` is never parsed as an anchor. v2 keeps
	 *  that key — it works and is tested — and adds the identity as a real
	 *  column, so the uniqueness rests on a field rather than on a convention
	 *  nobody downstream can see. */
	id: string;
	/** `NO_REF` ('') while the note does not exist yet — a composer opened from
	 *  the action sheet before its first commit. Once the note exists this is
	 *  its id, which is unique, so the slot can never collide.
	 *
	 *  Indexed so a note's buffer can be found without reconstructing the key. */
	note_id: string;
	/** The key-space string the id was built from: `note:${note_id}` for an
	 *  existing note, the anchor ref otherwise. NEVER parsed as an anchor. */
	ref: string;
	title: string;
	body: string;
	at: number;
}

/** Per (surface, device). id = `${surface}:${device_id}`.
 *  Deliberately NOT merged across devices: "where I am on my phone" and
 *  "where I am on my laptop" are different facts. The UI offers
 *  "continue where you left off on <device>" instead of jumping silently. */
export interface Position {
	id: string;
	surface: 'quran' | 'azkar';
	device_id: string;
	device_label: string;
	anchor: Anchor;
	start_pos: Pos;
	scroll_ratio: number;
	updated_at: number;
}

/** APPEND-ONLY. ~40 bytes/day. Gives khatmah progress and
 *  "what did I read in Ramadan 1447" for free, and can never conflict. */
export interface ReadMark {
	id: string;
	day: string;
	start_pos: Pos;
	end_pos: Pos;
	seconds: number;
	device_id: string;
}

/* ── Memorization: schema frozen in v1, UI in v2. Ids must be stable BEFORE
 *    anything writes, so freezing now costs nothing and the reverse costs a
 *    migration. id = `memo:${ayahId}`. ── */
export type MemoState = 'new' | 'learning' | 'review' | 'relearning' | 'suspended';

export interface MemoUnit extends Sync {
	ayah_id: AyahId;
	surah: number;
	ayah: number;
	state: MemoState;
	due_at: number;
	stability: number;
	difficulty: number;
	elapsed_days: number;
	scheduled_days: number;
	reps: number;
	lapses: number;
	last_review_at: number;
	confidence: 0 | 1 | 2 | 3;
}
// LWW would clobber FSRS state wholesale and make `reps` visibly go backwards.
// ReviewLog is append-only and holds the truth, so after every ingest the
// affected MemoUnits are RECOMPUTED by folding ReviewLog in reviewed_at order.
// See backup/rebuild.ts. The data was always there; only the reducer was missing.

export interface ReviewLog {
	id: string;
	memo_id: string;
	ayah_id: AyahId;
	reviewed_at: number;
	rating: 1 | 2 | 3 | 4;
	state_before: MemoState;
	elapsed_days: number;
	scheduled_days: number;
	duration_ms: number;
	device_id: string;
}

/** v2 hifz weakness map. Frozen now, zero UI cost. */
export interface Stumble {
	id: string;
	pos: Pos;
	ayah_id: number;
	at: number;
	memo_id: string | null;
	drill: string;
	device_id: string;
}

export interface DhikrDef extends Sync {
	name_ar: string;
	translit: string;
	translation: string;
	target: number; // 33 | 34 | 100 | custom — NEVER hardcoded 33/33/34
	category: 'morning' | 'evening' | 'after_salah' | 'istighfar' | 'salawat' | 'custom';
	source_id: string | null;
	sort_key: string;
}

/** APPEND-ONLY, IMMUTABLE. A tasbih total is SUM(reps) over these rows.
 *  This is the one place naive LWW is plainly WRONG: LWW on a mutable counter
 *  loses increments and the user watches their count go backwards. Merge here
 *  is set union and conflict is structurally impossible. */
export interface DhikrEvent extends Sync {
	dhikr_id: string;
	session_id: string;
	started_at: number;
	ended_at: number;
	reps: number; // may be negative for an explicit undo
	day: string;
}

export type Prayer = 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

export const PRAYERS = [
	'fajr',
	'dhuhr',
	'asr',
	'maghrib',
	'isha'
] as const satisfies readonly Prayer[];

/** No 'missed' as a state the app WRITES. An unlogged prayer is
 *  'not_recorded' forever and NEVER auto-flips after midnight —
 *  absence of a mark means nothing. 'prayed_late' is distinct from
 *  'qada' (made up later) and from 'on_time'. */
export type PrayerStatus =
	'jamaah' | 'on_time' | 'prayed_late' | 'qada' | 'not_recorded' | 'excused';

/** id = `${day}:${prayer}` — deterministic, so two devices recording the same
 *  fact produce the same row: not a conflict, and it cannot trip a unique index
 *  on import. `day` is a LOCAL calendar date string, never a UTC timestamp. */
export interface PrayerLog extends Sync {
	day: string;
	prayer: Prayer;
	status: PrayerStatus;
	logged_at: number;
	sunnah_before: 0 | 1;
	sunnah_after: 0 | 1;
	tz: string;
}
// The `note: string` field is DELETED. A reflection on a prayer is a real Note
// with an `ext:prayer_log:${day}:${prayer}` anchor — which makes it journalled,
// searchable, exportable, resurfaceable and protected, for free.

/** DAY-LEVEL FACTS. id = `${day}`. `prayer_log.id = ${day}:${prayer}` has no
 *  room for these, which is exactly why this must be frozen in M0.
 *  Consequences, wired the right way round: prayers missed during hayd/nifas
 *  are NOT made up; fasts ARE. Generating prayer qada debt for those days
 *  instructs a woman to perform prayers she is not required to perform.
 *  One neutral selector in the ordinary log. No euphemism. No separate
 *  "women's section". No prediction, no cycle length, no forecast, ever. */
export interface DayState extends Sync {
	day: string;
	state: 'normal' | 'hayd_nifas' | 'travel' | 'ill' | 'other';
}

/** id = `${day}`. Fed by day_state: a fast missed under hayd_nifas is owed. */
export interface FastLog extends Sync {
	day: string;
	kind: 'ramadan' | 'qada' | 'voluntary';
	status: 'fasted' | 'not_fasted' | 'owed' | 'made_up' | 'exempt';
}

/**
 * A settings row. NOTE THE ABSENT SYNC ENVELOPE — this is the only exported
 * table without one, and SPEC §3.3 freezes it that way (§3.6's worked example
 * shows two `settings` rows with exactly these four fields).
 *
 * THE MERGE RULE FOR SETTINGS, WRITTEN DOWN BECAUSE THE ROW SHAPE CANNOT
 * CARRY IT. `settings` IS exported and it carries `salah.madhab` and
 * `salah.method` — the two values the spec refuses to default because a wrong
 * one ships a Shafi'i Asr to a Hanafi user. With no `device_id` there is no
 * third field to break a tie with, so two rows for one key at the same
 * millisecond would have merged by ARRIVAL ORDER: non-deterministic, and
 * different on the two devices that produced them.
 *
 * The rule, implemented by `mergeSetting()` in `merge.ts` and property-tested
 * there, is the lexicographic maximum of
 *
 *     (updated_at, canonicalize(value), device_scoped)
 *
 * — later wins; at an exact tie the canonically greater value wins. That is
 * total, order-independent and computable from the frozen row shape, and the
 * LOSING row is still recorded in `conflicts` so a divergent `salah.madhab` is
 * something the user is shown rather than something that is silently picked.
 *
 * The alternative was to add `device_id: string` here and inherit every other
 * table's order. It was rejected because it changes a wire shape the spec
 * publishes verbatim, and the declared rule is equally deterministic.
 */
export interface Setting {
	key: string;
	value: unknown;
	updated_at: number;
	device_scoped: 0 | 1; // 1 => excluded from export (type scale, audio device)
}

export interface MetaRow {
	key:
		| 'schema_version'
		| 'anchor_space_version'
		| 'app_version'
		| 'corpus_version'
		| 'device_id'
		| 'device_label'
		| 'last_backup_ok_at'
		| 'last_backup_attempt_at'
		| 'last_backup_kind'
		| 'backup_dir_handle'
		| 'backup_dir_state' // 'ok'|'needs_regrant'|'none'
		| 'backup_highwater_rows'
		| 'persist_granted'
		| 'max_span'
		| 'notes_index'
		| 'quran_index'
		| 'audio_store_impl'
		| 'first_run_at'
		| 'install_ack_at'
		| 'lock_salt';
	value: unknown;
}

/* ═══════════ 3b. THE SETTINGS SHAPE ═══════════
 * SPEC §2.3: "The settings shape is frozen in M0. The UI lands in M6. Nothing
 * displays a prayer time until every row below is exposed."
 *
 * `settings` is a key/value table, so this block is a TYPE contract, not a
 * storage contract: adding a key later is a type-only change and never a
 * migration. That is deliberately the cheap direction to extend.
 *
 * The two rules this encodes that cannot be softened later:
 *   - `salah.madhab` and `salah.method` default to `null`, not to a school.
 *     `adhan@4.4.6` defaults to Madhab.Shafi; a silent default ships a Shafi'i
 *     Asr to every Hanafi user on earth. `null` means "not yet confirmed" and
 *     the times screen renders nothing until it is answered.
 *   - `salah.polar_resolution` defaults to 'unset' — a required explicit
 *     choice, never `Invalid Date` and never a silent substitution of Mecca.
 */

/** The 13 methods `adhan@4.4.6` actually ships, plus the two the app builds
 *  itself from `CalculationMethod.Other()`. Widening this union is a type-only
 *  change (settings values are `unknown` at rest), never a migration. */
export type SalahMethod =
	| 'MuslimWorldLeague'
	| 'Egyptian'
	| 'Karachi'
	| 'UmmAlQura'
	| 'Dubai'
	| 'MoonsightingCommittee'
	| 'NorthAmerica'
	| 'Kuwait'
	| 'Qatar'
	| 'Singapore'
	| 'Tehran'
	| 'Turkey'
	| 'Jafari' // NOT in adhan. Built from CalculationMethod.Other(): 16/14, maghrib +4.
	| 'Custom'; // the mandatory escape hatch — custom angles below

export type Madhab = 'shafi' | 'hanafi';
export type HighLatitudeRule =
	'auto' | 'middle_of_the_night' | 'seventh_of_the_night' | 'twilight_angle';
export type PolarResolution = 'unset' | 'aqrab_balad' | 'aqrab_ayyam' | 'mecca' | 'manual';

export interface SalahLocation {
	lat: number;
	lon: number;
	label: string;
	/** IANA zone. Every civil-day computation uses this, never the runtime's. */
	tz: string;
	/** ISO-3166 alpha-2, used ONLY as a first-run hint for madhab/method. */
	country: string;
}

/** Per-prayer ± minute offsets. Mosque boards differ; matching the local
 *  board is the correct behaviour. */
export interface SalahOffsets {
	fajr: number;
	sunrise: number;
	dhuhr: number;
	asr: number;
	maghrib: number;
	isha: number;
}

export interface SettingsMap {
	/* ── content (D3) ──────────────────────────────────────────────────── */
	/** D3: no Qur'an translation is bundled by default; Arabic tafsir instead.
	 *  The field exists regardless — it costs nothing and adding one later
	 *  under the M0 freeze rule would be a change to a frozen contract. */
	translation_id: string | null;
	tafsir_id: string | null;

	/* ── the global tracker switch (§2.3, frozen in M0) ─────────────────
	 * One tap turns off the prayer log, qada ledger, fast log, reading plan
	 * and lifetime tasbih totals. EVERY tracker reads this before it writes,
	 * from M0, so no tracker is ever written without it. */
	tracking_enabled: 0 | 1;

	/* ── salah (§2.3 — every row user-selectable, none silently inherited) */
	'salah.location': SalahLocation | null;
	/** NO SILENT DEFAULT. null until the user confirms it on first run. */
	'salah.madhab': Madhab | null;
	/** NO SILENT DEFAULT. null until confirmed; country inference is a hint. */
	'salah.method': SalahMethod | null;
	'salah.custom_fajr_angle': number | null;
	'salah.custom_isha_angle': number | null;
	'salah.custom_isha_interval_minutes': number | null;
	/** adhan honours shafaq only for MoonsightingCommittee. */
	'salah.shafaq': 'general' | 'ahmer' | 'abyad';
	'salah.offsets': SalahOffsets;
	/** 'auto' = OUR rule, `Math.abs(lat) > 48 → seventh_of_the_night`.
	 *  adhan's own `recommended()` is `latitude > 48` with no Math.abs. */
	'salah.high_latitude_rule': HighLatitudeRule;
	/** 'unset' is a REQUIRED explicit choice above ~66.5°, never a default. */
	'salah.polar_resolution': PolarResolution;
	'salah.hijri_calendar': 'islamic-umalqura';
	/** −2..+2. Applies to the LABEL and to Ramadan-dependent behaviour
	 *  (UmmAlQura +30 Isha, imsak, taraweeh) and NEVER to prayer-time input. */
	'salah.hijri_offset': -2 | -1 | 0 | 1 | 2;
	/** Drives Ja'fari params, midnight definition, maghrib delay, log grouping.
	 *  No inference — Sunni-configured unless the user opts in. */
	'salah.school': 'sunni' | 'jafari';
	/** Five discrete rows is Sunni-shaped by construction. */
	'salah.time_blocks': 5 | 3;
	'salah.witr_row': 0 | 1;
	'salah.imsak_display': 0 | 1;
	/** User-declared, NEVER inferred from a timezone change or GPS delta. */
	'salah.travel_mode': 0 | 1;

	/* ── qibla ─────────────────────────────────────────────────────────── */
	'qibla.convention': 'great_circle' | 'rhumb';
	/** "Match my mosque": stand facing the mihrab, tap once, store the offset. */
	'qibla.manual_bearing': number | null;

	/* ── notes ─────────────────────────────────────────────────────────── */
	'notes.resurfacing_enabled': 0 | 1;
	/** Journals are EXPORTED BY DEFAULT, with an opt-out. */
	'notes.export_revisions': 0 | 1;

	/* ── audio: a PREFERENCE, and it follows the user ──────────────────── */
	/** Which reciter the user likes is a preference, not a property of the
	 *  hardware in their hand, so it is NOT device-scoped and it syncs. (The
	 *  frozen file used to comment it as device-scoped while
	 *  `DEVICE_SCOPED_SETTINGS` excluded it — a policy constant disagreeing
	 *  with its own docblock. Resolved here in favour of syncing.) */
	'audio.reciter_id': string | null;

	/* ── device-scoped: excluded from export ─────────────────────────────
	 * A type scale chosen on a 6" phone must not follow the user onto a 27"
	 * monitor; a theme may legitimately differ per device; haptics exist on a
	 * phone and not on a laptop; an audio output device is a physical thing
	 * that is not there on the other machine. Every key in this block MUST
	 * also appear in `DEVICE_SCOPED_SETTINGS` below — asserted in
	 * `tests/db.spec.ts`, because a comment that disagrees with the constant
	 * it documents is how `ui.haptics` came to be described as device-scoped
	 * and exported anyway. */
	'ui.type_scale': number;
	'ui.theme': 'system' | 'light' | 'dark';
	'ui.haptics': 0 | 1;
	'audio.output_device': string | null;
}

export type SettingKey = keyof SettingsMap;

/** The defaults. `null` here always means "the user has not answered yet",
 *  never "we picked one for them". */
export const SETTING_DEFAULTS: SettingsMap = {
	translation_id: null,
	tafsir_id: null,
	tracking_enabled: 1,

	'salah.location': null,
	'salah.madhab': null,
	'salah.method': null,
	'salah.custom_fajr_angle': null,
	'salah.custom_isha_angle': null,
	'salah.custom_isha_interval_minutes': null,
	'salah.shafaq': 'general',
	'salah.offsets': { fajr: 0, sunrise: 0, dhuhr: 0, asr: 0, maghrib: 0, isha: 0 },
	'salah.high_latitude_rule': 'auto',
	'salah.polar_resolution': 'unset',
	'salah.hijri_calendar': 'islamic-umalqura',
	'salah.hijri_offset': 0,
	'salah.school': 'sunni',
	'salah.time_blocks': 5,
	'salah.witr_row': 0,
	'salah.imsak_display': 0,
	'salah.travel_mode': 0,

	'qibla.convention': 'great_circle',
	'qibla.manual_bearing': null,

	'notes.resurfacing_enabled': 1,
	'notes.export_revisions': 1,

	'audio.reciter_id': null,

	'ui.type_scale': 1,
	'ui.theme': 'system',
	'ui.haptics': 1,
	'audio.output_device': null
};

/** `device_scoped = 1` ⇒ the row is EXCLUDED from export. A type scale chosen
 *  on a 6" phone must not follow the user onto a 27" monitor.
 *
 *  This set and the "device-scoped" block of `SettingsMap` must list exactly
 *  the same keys. They did not: the block named five and the set held three, so
 *  `ui.haptics` was documented as device-scoped and exported anyway. */
export const DEVICE_SCOPED_SETTINGS = new Set<SettingKey>([
	'ui.type_scale',
	'ui.theme',
	'ui.haptics',
	'audio.output_device'
]);

export function isDeviceScoped(key: string): 0 | 1 {
	return DEVICE_SCOPED_SETTINGS.has(key as SettingKey) ? 1 : 0;
}

/* ═══════════ 3c. THE TABLE NAMES ═══════════
 * `rows` in a backup file is ONE FLAT ARRAY with a `t` discriminator (§3.6
 * note 3), not a `tables` object — which is what makes the digest domain
 * self-evident, makes the file shape identical to `SyncPort.since()`'s return
 * shape, and lets an importer skip an unknown FUTURE table by name while
 * reporting the count it skipped. These are those names.
 */
export const TABLES = [
	'notes',
	'tags',
	'note_tags',
	'note_revisions',
	'conflicts',
	'highlights',
	'bookmarks',
	'collections',
	'drafts',
	'positions',
	'read_marks',
	'memorization',
	'review_log',
	'stumbles',
	'dhikr_defs',
	'dhikr_events',
	'prayer_log',
	'day_state',
	'fast_log',
	'settings',
	'meta'
] as const;

export type TableName = (typeof TABLES)[number];

export function isTableName(value: unknown): value is TableName {
	return typeof value === 'string' && (TABLES as readonly string[]).includes(value);
}

/** The tables whose rows carry the §3.3 sync envelope, and therefore the only
 *  tables `mergeRow` is defined over. Everything else is either append-only
 *  (merged by union on a stable id), device-local, or a key/value store. */
export const SYNC_TABLES = [
	'notes',
	'tags',
	'note_tags',
	'highlights',
	'bookmarks',
	'collections',
	'memorization',
	'dhikr_defs',
	'dhikr_events',
	'prayer_log',
	'day_state',
	'fast_log'
] as const satisfies readonly TableName[];

export type SyncTableName = (typeof SYNC_TABLES)[number];

export function isSyncTable(value: unknown): value is SyncTableName {
	return typeof value === 'string' && (SYNC_TABLES as readonly string[]).includes(value);
}

/** APPEND-ONLY and IMMUTABLE once written. A row here is never updated, so a
 *  merge is set union on the primary key and conflict is structurally
 *  impossible. `memorization` is REBUILT from `review_log` after every ingest
 *  (§3.7) precisely because the log, not the folded state, is the truth.
 *
 *  `conflicts` belongs here. It is the ONLY place a losing paragraph survives,
 *  and it was in no class at all — so a conflict recorded on the phone never
 *  reached the laptop and did not survive a restore onto a wiped device. Its
 *  ids are already content-addressed (`cf:<reason>:<table>:<row_id>:<hash>`,
 *  see `merge.ts`), so union is both correct and free.
 *
 *  `dhikr_events` is in BOTH this list and `SYNC_TABLES` — it carries the sync
 *  envelope AND is immutable. That is not a contradiction, but it IS an
 *  ambiguity if the two sets are read as a partition, so the classification is
 *  `mergeClassOf` below and APPEND-ONLY IS CHECKED FIRST. A dispatcher written
 *  the obvious way, `isSyncTable(t) ? mergeBatch : unionBatch`, would apply LWW
 *  to the one table the spec singles out as the place naive LWW is plainly
 *  wrong: it loses increments, and the user watches their tasbih count go
 *  backwards. */
export const APPEND_ONLY_TABLES = [
	'note_revisions',
	'conflicts',
	'read_marks',
	'review_log',
	'stumbles',
	'dhikr_events'
] as const satisfies readonly TableName[];

export type AppendOnlyTableName = (typeof APPEND_ONLY_TABLES)[number];

export function isAppendOnlyTable(value: unknown): value is AppendOnlyTableName {
	return typeof value === 'string' && (APPEND_ONLY_TABLES as readonly string[]).includes(value);
}

/**
 * How a table's rows are combined when two databases meet. TOTAL over
 * `TABLES`, and evaluated IN THIS ORDER — append-only first.
 *
 *   'append_only'  union on the primary key. Immutable rows; nothing to lose.
 *   'sync'         `mergeRow` / `mergeBatch`: the LWW reducer with conflicts.
 *   'settings'     key/value with no sync envelope — `mergeSetting` in
 *                  `merge.ts` gives it the deterministic order the envelope
 *                  would otherwise have provided.
 *   'device_local' never merged, never exported across devices. "Where I am on
 *                  my phone" and "where I am on my laptop" are different facts.
 *
 * Having this be one total function rather than two overlapping sets is the
 * whole point: the ingest dispatcher cannot fall through, cannot double-count,
 * and cannot be written as a guess about which set to test first.
 */
export type MergeClass = 'append_only' | 'sync' | 'settings' | 'device_local';

export function mergeClassOf(table: TableName): MergeClass {
	if (isAppendOnlyTable(table)) return 'append_only';
	if (isSyncTable(table)) return 'sync';
	if (table === 'settings') return 'settings';
	return 'device_local';
}

/** The tables `mergeClassOf` sends to 'device_local'. Named so the assertion
 *  that the classification is total can be written as data, not as a negation. */
export const DEVICE_LOCAL_TABLES = [
	'drafts',
	'positions',
	'meta'
] as const satisfies readonly TableName[];

/* Which tables an EXPORT carries is a backup-layer policy (M4), not a schema
 * fact, and is deliberately not frozen here. What is frozen is the shape of
 * the row and the name it travels under. */

/* ═══════════ 4. DEXIE STORES ═══════════ */
export class GhurabaDB extends Dexie {
	notes!: Table<Note, string>;
	tags!: Table<Tag, string>;
	note_tags!: Table<NoteTag, string>;
	note_revisions!: Table<NoteRevision, string>;
	conflicts!: Table<Conflict, string>;
	highlights!: Table<Highlight, string>;
	bookmarks!: Table<Bookmark, string>;
	collections!: Table<Collection, string>;
	drafts!: Table<Draft, string>;
	positions!: Table<Position, string>;
	read_marks!: Table<ReadMark, string>;
	memorization!: Table<MemoUnit, string>;
	review_log!: Table<ReviewLog, string>;
	stumbles!: Table<Stumble, string>;
	dhikr_defs!: Table<DhikrDef, string>;
	dhikr_events!: Table<DhikrEvent, string>;
	prayer_log!: Table<PrayerLog, string>;
	day_state!: Table<DayState, string>;
	fast_log!: Table<FastLog, string>;
	settings!: Table<Setting, string>;
	meta!: Table<MetaRow, string>;

	/** `name` and `options` are widenings of the spec's `constructor()`, used
	 *  ONLY by tests to open isolated databases against fake-indexeddb.
	 *  `new GhurabaDB()` is unchanged and is what the app calls. */
	constructor(name: string = DB_NAME, options?: DexieOptions) {
		super(name, options);
		/* [updated_at+id] IS THE INDEX `SyncPort.since()` SCANS.
		 *
		 * SPEC §3.3 ("The port") specifies `since()` as a delta scan over
		 * `[updated_at+id]`; `port.ts` says so twice; `Sync.id`'s own comment
		 * above says "UUIDv7 — time-ordered, so [updated_at+id] scans stay
		 * cheap". No table declared it. The only compound available was
		 * `[deleted+updated_at]`, which cannot serve a scan across tombstones
		 * AND live rows in one pass.
		 *
		 * An implementer with no such index falls back to
		 * `where('updated_at').above(cursorTs)`, which SKIPS every row sharing
		 * the cursor's millisecond — two notes autosaved in the same tick, one
		 * exported and one silently never exported again, because `seal()` is
		 * asserted monotonic and will not go back for it. That is undetectable
		 * data loss in the export path, and adding the index later is a
		 * `version(2)` bump on every device.
		 *
		 * It is declared on all twelve SYNC_TABLES and nowhere else: the other
		 * nine tables have no `updated_at` field at all (`note_revisions`,
		 * `read_marks`, `review_log`, `stumbles`, `conflicts`, `meta`) or are
		 * device-local and never paged (`drafts`, `positions`, `settings`).
		 * `tests/db.spec.ts` asserts its presence on every SYNC_TABLE so a later
		 * edit cannot quietly drop it. */
		this.version(1).stores({
			notes:
				'id, updated_at, [updated_at+id], [deleted+updated_at], start_pos, end_pos, ' +
				'[deleted+start_pos], collection_id, pinned, private, resurface_at',
			tags: 'id, updated_at, [updated_at+id], [deleted+updated_at], name',
			note_tags:
				'id, note_id, tag_id, [tag_id+deleted], updated_at, [updated_at+id], ' +
				'[deleted+updated_at]',
			note_revisions: 'id, note_id, [note_id+idx], [note_id+at]',
			conflicts: 'id, table, row_id, [table+resolved], created_at',
			highlights:
				'id, updated_at, [updated_at+id], [deleted+updated_at], start_pos, end_pos, ' +
				'color, note_id',
			bookmarks:
				'id, updated_at, [updated_at+id], [deleted+updated_at], start_pos, ' +
				'collection_id, [collection_id+sort_key]',
			collections: 'id, updated_at, [updated_at+id], [deleted+updated_at], parent_id, sort_key',
			drafts: 'id, ref, at',
			positions: 'id, surface, updated_at',
			read_marks: 'id, day, [day+start_pos]',
			memorization:
				'id, ayah_id, updated_at, [updated_at+id], [deleted+updated_at], due_at, ' +
				'[state+due_at]',
			review_log: 'id, memo_id, [memo_id+reviewed_at], ayah_id',
			stumbles: 'id, pos, ayah_id, at, [ayah_id+at]',
			dhikr_defs: 'id, updated_at, [updated_at+id], [deleted+updated_at], category, sort_key',
			dhikr_events: 'id, dhikr_id, [dhikr_id+day], day, session_id, updated_at, [updated_at+id]',
			prayer_log: 'id, day, updated_at, [updated_at+id], [deleted+updated_at]',
			day_state: 'id, day, updated_at, [updated_at+id], [deleted+updated_at]',
			fast_log: 'id, day, kind, updated_at, [updated_at+id], [deleted+updated_at]',
			settings: 'key, updated_at',
			meta: 'key'
		});

		/* ─────────────────────────────────────────────────────────────────
		 * v2 — taken deliberately, before M4 ships export/import, because a
		 * schema change is free until backup files exist in the world and
		 * expensive the moment they do. Recorded in DECISIONS.md D7.
		 *
		 * Three changes, all additive. No data is rewritten except to fill the
		 * new columns with their "nothing happened" values, so a v1 database
		 * upgrades in place and a v1 export still imports.
		 *
		 * 1. `note_revisions.deleted` / `.deleted_at` — the privacy hole. See
		 *    the field docs on `NoteRevision`.
		 * 2. `drafts.note_id` — two notes on one anchor shared a draft slot.
		 * 3. `[note_id+device_id+idx]` replaces nothing and ADDS what the pin
		 *    rule actually wants. `idx` is a PER-DEVICE counter, not a per-note
		 *    sequence, so after a merge the values duplicate and have gaps and
		 *    `[note_id+idx]` cannot fetch "revision n" or order a journal. It is
		 *    left in place (dropping an index is a rewrite for no gain) but it
		 *    is no longer the one to reach for.
		 * ───────────────────────────────────────────────────────────────── */
		this.version(2)
			.stores({
				note_revisions:
					'id, note_id, [note_id+idx], [note_id+at], [note_id+deleted], ' +
					'[note_id+device_id+idx]',
				drafts: 'id, ref, at, note_id'
			})
			.upgrade(async (tx) => {
				await tx
					.table('note_revisions')
					.toCollection()
					.modify((row: NoteRevision) => {
						// Every pre-existing revision is live by definition: v1 had no way
						// to express a deletion, so nothing can have been deleted.
						row.deleted = 0;
						row.deleted_at = 0;
					});
				await tx
					.table('drafts')
					.toCollection()
					.modify((row: Draft) => {
						// A v1 draft was keyed by ref alone. It belongs to whichever
						// composer reopens that anchor, which is what v1 meant by it —
						// so NO_REF is the honest backfill, not a guess at a note id.
						row.note_id = NO_REF;
					});
			});
	}
}
export const db = new GhurabaDB();

/*
 * Notes on what changed and why, in one place (SPEC §3.3):
 *
 * - No `*tags` multiEntry index. `Note.tags: string[]` is gone. A multiEntry
 *   index aborts the entire transaction if one imported value is not a valid
 *   IndexedDB key — one malformed row makes a whole backup permanently
 *   unimportable. And set membership under row-level LWW silently loses tags.
 *   `note_tags` with deterministic ids merges by union and cannot conflict.
 * - `prayer_log.note` deleted. It is a real Note now.
 * - `&[day+prayer]` and `&ayah_id` unique indexes dropped. The deterministic
 *   primary key already carries the guarantee, and a unique index is a way for
 *   an import to fail on data that is semantically fine.
 * - Attachments: declared NO for v1. Added later by a plain `version(2)
 *   .stores()` addition with no data migration, since the table would be empty.
 *   An undecided table is worse than a declared "no".
 */
